import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

const userProfileSchema = z.object({
  ad_count: z.number(),
  analytics_tier: z.string(),
  annual_amazon_sales: z.string().nullable(),
  brand_registered: z.string(),
  can_have_multiple_aa_credentials: z.boolean(),
  channel: z.string(),
  content_tier: z.string(),
  current_profile: z.string(),
  customer_type: z.string().nullable(),
  default_marketplace: z.record(z.string(), z.string()).nullable(),
  digital_shelf_limit: z.number(),
  email: z.string(),
  email_verified_on: z.string().nullable(),
  full_user_name: z.string(),
  icp_status: z.string().nullable(),
  is_organization_admin: z.boolean(),
  is_staff: z.boolean(),
  latest_tos_agreement_status: z.string(),
  login_method: z.string(),
  modules: z
    .object({
      id: z.number(),
      name: z.string(),
      settings: z
        .object({
          only: z.boolean().optional(),
        })
        .nullable(),
    })
    .array(),
  old_organization_payment_required: z.boolean(),
  org_status: z.string(),
  organization_created_date: z.string(),
  organization_id: z.string(),
  organization_name: z.string(),
  organization_type: z.string().nullable(),
  organization_subtype: z.string().nullable().optional(),
  pb_tier: z.string(),
  pricing_tier: z.string(),
  product_identifier: z.string(),
  promotions_tier: z.string(),
  preferences: z.object({
    acos_or_roas: z.literal("acos").or(z.literal("roas")),
    all_notifications: z.boolean().optional(),
    merchandise_dashboard: z.record(z.string(), z.boolean()).optional(),
    cogs: z.literal("classic").or(z.literal("landed_cost")).optional(),
  }),
  prefers_org_name_display: z.boolean(),
  tier: z.string(),
  tos_acceptance_date: z.string().nullable(),
  total_monthly_sales: z.string().nullable(),
  total_sales_start_date: z.record(z.string(), z.string()),
  user_id: z.string(),
  memberships: z.array(
    z.object({
      membership_id: z.number(),
      membership_marketplace: z.string().nullable(),
      membership_role_id: z.number(),
      membership_role_org_id: z.number(),
      membership_role_name: z.string(),
      membership_role_permissions: z.array(z.string()),
    }),
  ),
  account_manager: z.object({
    id: z.number(),
    agency_id: z.number().nullable(),
  }),
  language: z.string().optional(),
  initial_data_loaded_on: z.string().datetime().nullable(),
});

export type UserProfile = z.infer<typeof userProfileSchema>;

export const userProfileApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "putUserProfileMarketplaces",
    method: "put",
    path: "/userprofile/",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({
          default_marketplace: z.record(z.string(), z.string()),
        }),
      },
    ],
    status: 200,
    // The endpoint answers with a bare JSON string ("Updated")
    response: z.string(),
  })
    .addEndpoint({
      alias: "getUserProfile",
      method: "get",
      path: "/userprofile/",
      status: 200,
      response: userProfileSchema,
    })
    .addEndpoint({
      alias: "changeStaffUserOrg",
      method: "put",
      path: "/manage/userprofile/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: z.object({
            org: z.union([z.number(), z.string()]),
          }),
        },
      ],
      status: 200,
      response: z.object({}),
    })
    .build(),
);
